// Umami アクセス解析スクリプトの動的注入 + 型安全なカスタムイベント送信ラッパー。
// Datadog（shared/lib/datadog.ts）と同様、副作用インポートで `script` を注入する。
//
// 計装方針:
// - エラートラッキングは Umami ではなく Datadog 側で扱うため、Umami へ送るのは
//   「ユーザーが主要機能を実行した」シグナルだけに絞る（admin/analytics ダッシュボードの
//   ① 機能利用率・定着分析の母集団になる）。
// - 環境ごとの送信可否は `VITE_UMAMI_WEBSITE_ID_DNR` の有無で制御する（環境変数が
//   未設定の環境では no-op）。
// - E2E テスト中は `?umami=disabled` クエリで送信を抑止。
// - 詳細ページ（`/projects/<uuid>` 等）の auto-tracked pageview は URL に動的 ID が
//   含まれるため、Umami 側で 1 URL = 1 ページとしてカウントされてしまう。
//   `data-before-send` ハンドラで UUID セグメントを `/{id}` に正規化し、
//   ページパターン単位で集約する。

import { VITE_UMAMI_WEBSITE_ID_DNR } from '@/shared/lib/env';

// ---------------------------------------------------------------------------
// イベント定義（追加する場合は backend の admin/umami/feature-usage で集計される
// event_name と一致させること: modules/admin/umami/lib/feature_domain.py 参照）
// ---------------------------------------------------------------------------

export const UmamiEvent = {
  PROJECT_CREATED: 'project_created',
  ASSESSMENT_CREATED: 'assessment_created',

  // AI チャット（/ai-chat の汎用チャット）。THREAD_STARTED は「スレッド着手＝定着シグナル」、
  // MESSAGE_SENT は「同一スレッドのメッセージ送信数＝利用深度」として併存させる。
  AI_CHAT_THREAD_STARTED: 'ai_chat_thread_started',
  AI_CHAT_MESSAGE_SENT: 'ai_chat_message_sent',

  // 事業詳細サイドパネルの AI チャット。/ai-chat と分離して集計するため別イベント名にする。
  AI_CHAT_PROJECT_THREAD_STARTED: 'ai_chat_project_thread_started',
  AI_CHAT_PROJECT_MESSAGE_SENT: 'ai_chat_project_message_sent',

  // 事業詳細画面の AI 分析（chat_type × 初回/再実行）。
  // 同一事業で何回叩いたかは data.project_id で集計する。
  NATIONAL_GRANTS_MATCH_EXECUTED: 'national_grants_match_executed',
  NATIONAL_GRANTS_MATCH_RE_EXECUTED: 'national_grants_match_re_executed',
  PREFECTURAL_GRANTS_MATCH_EXECUTED: 'prefectural_grants_match_executed',
  PREFECTURAL_GRANTS_MATCH_RE_EXECUTED: 'prefectural_grants_match_re_executed',
  PREFECTURAL_SPECIAL_ALLOCATION_TAXES_MATCH_EXECUTED:
    'prefectural_special_allocation_taxes_match_executed',
  PREFECTURAL_SPECIAL_ALLOCATION_TAXES_MATCH_RE_EXECUTED:
    'prefectural_special_allocation_taxes_match_re_executed',

  // bulk（複数事業一括）AI 分析。事業詳細単体起動と区別するため別イベントにする。
  BULK_NATIONAL_GRANTS_EXECUTED: 'bulk_national_grants_executed',
  BULK_PREFECTURAL_GRANTS_EXECUTED: 'bulk_prefectural_grants_executed',
  BULK_PREFECTURAL_SPECIAL_ALLOCATION_TAXES_EXECUTED:
    'bulk_prefectural_special_allocation_taxes_executed',

  SETTLEMENT_CONVERSION_EXECUTED: 'settlement_conversion_executed',
  DATA_IMPORT_EXECUTED: 'data_import_executed',
} as const;

export type UmamiEventName = (typeof UmamiEvent)[keyof typeof UmamiEvent];

// Umami の event_data は string / number / Date / boolean のプリミティブのみ受け付ける。
// 配列・ネストオブジェクトは Umami 側で型エラーになるため許容しない。
export type UmamiEventData = Record<string, string | number | boolean | null | undefined>;

// ---------------------------------------------------------------------------
// テナントコンテキスト（全カスタムイベントへ自動付与）
// ---------------------------------------------------------------------------
//
// admin の分析画面で event_name × テナントの内訳を集計できるよう、全イベントに
// テナント情報を付与する。プロパティ名は backend の event-data 集計（property_name）と
// 一致させること（modules/admin/umami/ 配下）。
//
// shared 層は FSD 上 entities/session を import できないため、ログイン確定時に上位層
// （app）から `setUmamiTenantContext` で注入する設計にしている。

// 付与するプロパティのキー（backend と一致させる single source）。
export const UMAMI_TENANT_PROPERTY = {
  ID: 'tenant_id',
  CODE: 'tenant_code',
  NAME: 'tenant_name',
} as const;

export type UmamiTenantContext = {
  [UMAMI_TENANT_PROPERTY.ID]: string;
  [UMAMI_TENANT_PROPERTY.CODE]: string;
  [UMAMI_TENANT_PROPERTY.NAME]: string;
};

let tenantContext: UmamiTenantContext | null = null;

/**
 * 全カスタムイベントに自動付与するテナントコンテキストを設定する。
 *
 * - ログイン / セッション確定時に tenant 情報を渡す
 * - ログアウト時は `null` を渡してクリアする（次ログインまで付与しない）
 */
export const setUmamiTenantContext = (ctx: UmamiTenantContext | null): void => {
  tenantContext = ctx;
};

// ---------------------------------------------------------------------------
// URL 正規化（詳細ページ集約）
// ---------------------------------------------------------------------------

// UUID v1〜v5 形式のパスセグメントを `{id}` に置換し、`/projects/<uuid>` / `/assessment/<uuid>` 等の
// 詳細ページを 1 つの URL パターンに集約する。
// 末尾・スラッシュ前・クエリ前のいずれの位置でもマッチさせる。
const UUID_SEGMENT_PATTERN =
  /\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?=\/|\?|#|$)/gi;

export const normalizeUmamiUrl = (url: string): string => {
  return url.replace(UUID_SEGMENT_PATTERN, '/{id}');
};

// ---------------------------------------------------------------------------
// before-send ハンドラ（auto-tracked pageview / custom event 共通で URL を正規化）
// ---------------------------------------------------------------------------

type UmamiBeforeSendPayload = {
  url?: string;
  referrer?: string;
  [key: string]: unknown;
};

const umamiBeforeSend = (
  _type: string,
  payload: UmamiBeforeSendPayload,
): UmamiBeforeSendPayload => {
  if (typeof payload.url === 'string') {
    payload.url = normalizeUmamiUrl(payload.url);
  }
  if (typeof payload.referrer === 'string') {
    payload.referrer = normalizeUmamiUrl(payload.referrer);
  }
  return payload;
};

// ---------------------------------------------------------------------------
// window.umami 型定義（公式 SDK の最小サブセット）
// ---------------------------------------------------------------------------

type UmamiGlobal = {
  track: ((name: string, data?: UmamiEventData) => Promise<unknown>) | undefined;
};

declare global {
  interface Window {
    umami?: UmamiGlobal;
    // Umami スクリプトが `data-before-send` 属性で参照するグローバル関数
    __umamiBeforeSend?: typeof umamiBeforeSend;
  }
}

// ---------------------------------------------------------------------------
// E2E ガード
// ---------------------------------------------------------------------------

/**
 * `?umami=disabled` がクエリにある場合 Umami の送信を抑止する。
 * Playwright での起動時 `goto('/?umami=disabled')` で利用する。
 *
 * SSR や jsdom 環境などで `window.location` が存在しないケースも考慮し try/catch。
 */
export const isUmamiDisabled = (): boolean => {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get('umami') === 'disabled';
  } catch {
    return false;
  }
};

// ---------------------------------------------------------------------------
// 初期化（スクリプト注入）
// ---------------------------------------------------------------------------

if (VITE_UMAMI_WEBSITE_ID_DNR && !isUmamiDisabled()) {
  // スクリプト読み込み前に before-send ハンドラをグローバル登録する必要がある
  // （Umami スクリプトが初回 pageview を送信する時点で参照されるため）
  window.__umamiBeforeSend = umamiBeforeSend;

  const script = document.createElement('script');
  script.defer = true;
  script.src = '/umami/fetch.js';
  script.dataset.websiteId = VITE_UMAMI_WEBSITE_ID_DNR;
  script.dataset.beforeSend = '__umamiBeforeSend';
  document.head.appendChild(script);
}

// ---------------------------------------------------------------------------
// 送信 API
// ---------------------------------------------------------------------------

/**
 * Umami へカスタムイベントを送信する。
 *
 * - 環境変数で website_id が未設定の場合は no-op
 * - `window.umami` が未ロード（CSR 起動直後・スクリプトブロック環境）でも no-op で安全に終了する
 * - E2E テスト中（`?umami=disabled` クエリ付き起動）も no-op
 * - 戻り値は意図的に void（fire and forget）。送信失敗を呼び出し側で扱わない
 */
export const trackEvent = (name: UmamiEventName, data?: UmamiEventData): void => {
  if (!VITE_UMAMI_WEBSITE_ID_DNR || isUmamiDisabled()) {
    return;
  }
  const track = window.umami?.track;
  if (typeof track !== 'function') {
    return;
  }
  // テナントコンテキストを基底に、イベント固有 data を後置きでマージする
  // （同名キーは個別イベントの値を優先）。コンテキスト・data が共に無い場合は
  // 従来どおり undefined を渡す。
  const payload = tenantContext || data ? { ...tenantContext, ...data } : undefined;
  try {
    // .catch を付けないと Umami 側の reject（ネットワーク障害・タイムアウト等）が
    // unhandledrejection として残り、Datadog RUM やブラウザコンソールにノイズが流入する。
    // try/catch は同期例外（track 内部の throw）のみ捕捉する保険。
    void track(name, payload).catch(() => {});
  } catch {
    // Umami の送信失敗はユーザー体験に影響させない（fire and forget）
  }
};
